<script setup>
import { ref, watch } from 'vue';
import axios from 'axios';
import notify from '@/store/Common/notify';
import { useWhiteLabel } from '@/store/useWhitelabel';
import TabPreview from '@/views/Admin/Config/Settings/Common/TabPreview.vue';

const { logo, favicon, appNameDisplay, useGradient, reverseGradient, setPreviewFavicon } = useWhiteLabel();

const uploading = ref(false);
const image = ref();
const errors = ref([]);
const previewUrl = ref(null);

watch(image, (newFile) => {
    if (newFile) {
        previewUrl.value = URL.createObjectURL(newFile);
        setPreviewFavicon(previewUrl.value);
    }
});

const props = defineProps({
    cardTitle: {
        type: String,
        required: true
    },

    formId: {
        type: String,
        required: true
    },

    formName: {
        type: String,
        required: true
    },

    itemSlug: {
        type: String,
        required: true
    }
});

function saveImage() {
    uploading.value = true;
    errors.value = [];
    const formData = new FormData();
    formData.append(props.itemSlug, image.value);

    axios.post('/api/settings/' + props.itemSlug + '/upload', formData)
        .then((response) => {
            notify(response.data.message ?? 'Updated Successfully', 'success');
        })
        .catch((e) => {
            if (e.response?.data?.errors) {
                Object.values(e.response.data.errors).forEach((error) => {
                    error.forEach((err) => {
                        notify(err, 'error');
                        errors.value.push(err);
                    });
                });
            } else {
                notify('An unexpected error occurred.', 'error');
            }
        })
        .finally(() => {
            uploading.value = false;
        });
}

function clearInput() {
    errors.value = [];
    previewUrl.value = props.itemSlug === 'favicon' ? favicon.value : logo.value;
}
</script>

<template>
    <v-card class="SettingCard">
        <v-card-title>
            {{ cardTitle }}
        </v-card-title>
        <v-card-text>
            <div class="BrandingImageUpload">
                <div class="BrandingImageUpload__preview">
                    <div
                        v-if="itemSlug === 'logo'"
                        class="BrandingImageUpload__image__logo"
                        :class="{'--secondary':useGradient && !reverseGradient}"
                    >
                        <img
                            :src="previewUrl ?? logo"
                            alt=""
                        />
                    </div>
                    <div
                        v-if="itemSlug === 'favicon'"
                        class="BrandingImageUpload__image__favicon"
                    >
                        <TabPreview
                            :icon="previewUrl ?? favicon"
                            :title="appNameDisplay"
                        />

                    </div>
                </div>

                <div class="BrandingImageUpload__form">
                    <form
                        :id="formId"
                        :name="formName"
                        @submit.prevent="saveImage"
                    >
                        <v-file-input
                            v-bind="$attrs"
                            v-model="image"
                            truncate-length="15"
                            outlined
                            :prepend-icon="errors.length > 0 ? 'mdi-alert-circle' : 'mdi-image'"
                            persistent-hint
                            :error="errors.length > 0"
                            :error-messages="errors"
                            @click:clear="clearInput"
                        />
                    </form>
                </div>
            </div>
        </v-card-text>
        <v-card-actions>
            <v-spacer />
            <v-btn
                :form="formId"
                type="submit"
                color="primary"
                :disabled="uploading"
            >
                <v-icon
                    small
                    left
                >
                    {{ uploading ? 'fas fa-spin fa-spinner' : 'fas fa-save' }}
                </v-icon>
                Save
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<style scoped
       lang="scss">

.BrandingImageUpload {
    display: grid;
    grid-template-columns: 1fr;

    @media (min-width: 768px) {
        grid-template-columns: 1fr 3fr;
        gap: 48px;
    }
}

.BrandingImageUpload__image__logo {
    flex: 0 0 100%;
    width: 100%;
    margin-bottom: 24px;
    padding: 24px;
    background-color: var(--primary-500);

    &.--secondary {
        background-color: var(--secondary-500);
    }

    img {
        width: 100% !important;
    }
}

.BrandingImageUpload__image__favicon {
    flex: 0 0 100%;
    width: 100%;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
}


</style>